<template>
  <div class="invoice-add-wrap">
    <portal to="dash-modal">
      <ImportToInvoiceModal
        v-if="showImport"
        :invoice="invoice"
        @close="closeImportModal"
        @add-lines="addLines"
      />

      <InvoiceDesignModal
        v-if="showDesign"
        :invoice="prepareInvoicePreview()"
        :totals="getTotals()"
        :recipient="(selectedCustomer) ? selectedCustomer.id : null"
        @close="closeDesignModal"
      />
      
      <ImportKvkModal
        v-if="showImportKvkModal"
        :target-data="targetData"
        @close-modal="showImportKvkModal = false"
      />

      <BrandModal 
        v-if="showEditBrandModal"
        :selectedBrand="(selectedBrand) ? selectedBrand.id: null"
        @close="closeBrandModal()"
        @edited="onBrandUpdated"
        @deleted="onBrandDeleted"
        @created="onBrandCreated"
      />

      <ProjectModal 
        v-if="showAddProjectModal && selectedCustomer"
        :customer-id="(selectedProject) ? null : selectedCustomer.id"
        :project-id="(selectedProject) ? selectedProject.id: null"
        force-open
        @cancel="closeProjectModal"
        @created="onProjectCreated"
        @updated="onProjectUpdated"
        @deleted="onProjectDeleted"
      />

      <ContactModalSimple 
        v-if="showContactModal"
        :new-customer="newCustomer"
        :contact="selectedCustomer"
        @close-modal="showContactModal = false"
        @created="prepareUpdatedCustomer"
        @updated="prepareUpdatedCustomer"
        @deleted="resetCustomer"
      />

      <InvoiceDetailsModal 
        v-if="invoice &&showInvoiceDetailsModal"
        :invoice="invoice"
        @close="showInvoiceDetailsModal = false"
      />
    </portal>
              
    <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
      <div class="invoice-wrap-bg">
        <LoadingSpinner v-if="loading" />
        <div v-if="!loading" class="invoice-wrap">
          <div class="invoice-section step-1" :class="{ 'has-customer': selectedCustomer }">
            <div class="step-1--customer-wrap">
              <div class="step-1--sender-wrap">
                <div class="invoice-section--label">{{ $gettext('Invoice sender') }}</div>

                <!-- Company info -->
                <div class="step-1--sender" style="flex: 1; flex-direction: column;" :class="{ 'has-brand': selectedBrand }">
                  <div @click.prevent="openEditSenderModal()" style="display: flex; flex-direction: column; gap: 1rem; cursor: pointer; padding: 0.5rem;">
                    
                    <div style="display: flex; gap: 1rem;">
                      <div v-if="!selectedBrand" class="step-1--sender__logo">
                        <img v-if="currentCompany.logo" :src="currentCompany.logo_thumbnail" :alt="currentCompany.name">
                        <div v-else class="no-logo">
                          {{ $gettext('No logo') }}
                        </div>
                      </div>
                      <div v-if="selectedBrand" class="step-1--sender__logo">
                        <img v-if="selectedBrand.brand_logo" :src="selectedBrand.brand_logo" :alt="selectedBrand.name">
                        <div v-else class="no-logo">
                          {{ $gettext('No logo') }}
                        </div>
                      </div>

                      <div v-if="!selectedBrand" class="step-1--sender__info">
                        <div v-if="currentCompany.name" class="current-company-name">
                          <h4>{{ currentCompany.name }}</h4>
                        </div>
                        <div v-if="currentCompany.address">{{ currentCompany.address }}</div>
                        <div v-if="currentCompany.postal_code || currentCompany.city">
                          {{ (currentCompany.postal_code) ? currentCompany.postal_code : '' }} 
                          {{ (currentCompany.city) ? currentCompany.city : '' }}
                        </div>
                        <div v-if="currentCompany.country_name && currentCompany.show_country_on_invoice">{{ currentCompany.country }}</div>
                        <div v-if="currentCompany.website && currentCompany.website_show_on_invoice">{{ currentCompany.website }}</div>
                        <div v-if="currentCompany.phone_number && currentCompany.phone_number_show_on_invoice">{{ currentCompany.phone_number }}</div>
                        <div v-if="currentCompany.kvk">{{ currentCompany.kvk }}</div>
                        <div v-if="currentCompany.btw && currentCompany.btw_show_on_invoice">{{ currentCompany.btw }}</div>
                        <div v-if="currentCompany.bank_account_number">{{ currentCompany.bank_account_number }}</div>
                        <div v-if="currentCompany.companysettings.swift_number && currentCompany.companysettings.show_swift_on_invoice">{{ currentCompany.companysettings.swift_number }}</div>
                      </div>

                      <div v-if="selectedBrand" class="step-1--sender__info">
                        <div v-if="selectedBrand.name" class="current-company-name">
                          <h4>{{ selectedBrand.name }}<br><span class="brands-company">({{ currentCompany.name + ' brand' }})</span></h4>
                          
                        </div>
                        <div v-if="selectedBrand.brand_address">{{ selectedBrand.brand_address }}</div>
                        <div v-if="selectedBrand.brand_postal_code || selectedBrand.brand_city">
                          {{ (selectedBrand.brand_postal_code) ? selectedBrand.brand_postal_code : '' }} 
                          {{ (selectedBrand.brand_city) ? selectedBrand.brand_city : '' }}
                        </div>
                        <div v-if="selectedBrand.brand_country && currentCompany.show_country_on_invoice">{{ selectedBrand.brand_country }}</div>
                        <!-- field 0 website -->
                        <div v-if="selectedBrand.field_0 && currentCompany.website_show_on_invoice">
                          <span v-if="selectedBrand.label_0" class="field-label">{{ selectedBrand.label_0 }}:</span>
                          {{ selectedBrand.field_0 }}
                        </div>
                        <!-- field 1 phone_number -->
                        <div v-if="selectedBrand.field_1 && currentCompany.phone_number_show_on_invoice">
                          <span v-if="selectedBrand.label_1" class="field-label">{{ selectedBrand.label_1 }}:</span>
                          {{ selectedBrand.field_1 }}
                        </div>
                        <!-- field 2 kvk -->
                        <div v-if="selectedBrand.field_2">
                          <span v-if="selectedBrand.label_2" class="field-label">{{ selectedBrand.label_2 }}:</span>
                          {{ selectedBrand.field_2 }}
                        </div>
                        <!-- field 3 btw -->
                        <div v-if="selectedBrand.field_3 && currentCompany.btw_show_on_invoice">
                          <span v-if="selectedBrand.label_3" class="field-label">{{ selectedBrand.label_3 }}:</span>
                          {{ selectedBrand.field_3 }}
                        </div>
                        <!-- field 4 bank_account_number -->
                        <div v-if="selectedBrand.field_4">
                          <span v-if="selectedBrand.label_4" class="field-label">{{ selectedBrand.label_4 }}:</span>
                          {{ selectedBrand.field_4 }}
                        </div>
                        <!-- field 5 swift_number -->
                        <div v-if="selectedBrand.field_5 && currentCompany.companysettings.show_swift_on_invoice">
                          <span v-if="selectedBrand.label_5" class="field-label">{{ selectedBrand.label_5 }}:</span>
                          {{ selectedBrand.field_5 }}
                        </div>
                      </div>
                      
                    </div>

                    <b-alert v-if="showKvkWarning"  show class="gk-alert mb-0" variant="warning">
                      <div>
                        This invoice is missing some information for it to be considered a legal invoice: <b>{{ companyMissingFieldsText }}</b>. You can <a href='#' @click.prevent="showImportKvkModal = true" target='_blank'>add this information now</a>.
                      </div>
                    </b-alert>
                  </div>

                  <GekkoButton
                    v-if="selectedBrand"
                    icon="fa-solid fa-xmark"
                    variant="link"
                    class="step-1-close"
                    @click="selectBrand(null)"
                  />
                  <div class="invoice-brand-select-wrap" :class="{ 'show-selector': (showBrandsSelector && !selectedBrand) }">
                    <GekkoButton
                      v-if="!showBrandsSelector"
                      :text="$gettext('Select brand')"
                      variant="link"
                      @click="showBrandsSelector = true"
                      size="sm"
                      class="w-100 p-0"
                    />
                    <div v-if="showBrandsSelector && !selectedBrand && availableBrands && availableBrands.length > 0">
                      <div class="invoice-section--label" style="position: relative;">
                        {{ $gettext('Brand') }}

                        <GekkoButton
                          icon="fa-solid fa-xmark"
                          variant="link"
                          class="step-1-close"
                          style="position: relative; top: 2px; right: auto; margin-left: 2px;"
                          @click="showBrandsSelector = false"
                        />

                      </div>
                      <div class="text-right">
                        <BrandSelector :preselect="selectedBrand" @change="selectBrand" />
                        <GekkoButton
                          @click="showEditBrandModal = true"
                          :text="$gettext('Add new brand')"
                          :icon="'fa-solid fa-plus'"
                          variant="link"
                          class="px-0 ml-auto"
                          size="sm"
                        />
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div><!-- end step-1--sender-wrap -->
              
              <div class="step-1--recipient-wrap">
                <div class="invoice-section--label">{{ $gettext('Issued to') }}</div>
                <div v-if="!selectedCustomer && !loadingCustomer" class="text-right">
                  <CustomerSelect 
                    :preselect="preselectedCustomer"
                    :exclude="[currentCompany.id]"
                    allow-add searchable 
                    @selected-customer="selectCustomerAndProject"
                    @add-customer="(customerName) => { newCustomer = customerName; showContactModal = true }"
                  />
                  <GekkoButton
                    @click="showContactModal = true"
                    :text="$gettext('Add new customer')"
                    :icon="'fa-solid fa-plus'"
                    variant="link"
                    class="px-0 ml-auto"
                    size="sm"
                  />
                </div>
                <div v-else class="step-1--customer">
                  <LoadingSpinner v-if="loadingCustomer" />
                  <div v-else class="step-1--customer__customer" @click="showContactModal = true">
                    <div v-if="selectedCustomer.name">
                      <h4>{{ selectedCustomer.name }}</h4>
                    </div>

                    <!-- <div v-if="selectedProject">{{ selectedProject.name }}</div> -->
                    <!-- <div v-if="selectedProject && selectedProject.contact_name">{{ selectedProject.contact_name }}</div> -->
                    <!-- <div v-else-if="selectedCustomer.contact_name">{{ selectedCustomer.contact_name }}</div> -->

                    <div v-if="selectedCustomer.contact_name">{{ selectedCustomer.contact_name }}</div>

                    <div v-if="selectedCustomer.address">
                      {{ selectedCustomer.address }}
                      {{ (selectedCustomer.postal_code) ? ', ' + selectedCustomer.postal_code : '' }}
                      {{ (selectedCustomer.city) ? ', ' + selectedCustomer.city : '' }}
                      {{ (selectedCustomer.country_name && selectedCustomer.show_country_on_invoice) ? ', ' + selectedCustomer.country : '' }}
                    </div>
                    
                    <div v-if="selectedCustomer.btw">{{ selectedCustomer.btw }}</div>
                    <div v-if="selectedCustomer.kvk">{{ selectedCustomer.kvk }}</div>
                    <GekkoButton
                      v-if="!isEdit"
                      icon="fa-solid fa-xmark"
                      variant="link"
                      class="step-1-close"
                      v-gk-confirm="{
                        title: $gettext('Change customer'),
                        message: $gettext('Selecting a different customer will reset the invoice lines'),
                        options: {
                            variant: 'primary',
                            confirmText: $gettext('Ok')
                        },
                        callback: resetCustomer
                      }"
                    />
                  </div>
                </div>
              </div>

              <div class="step-1--project-wrap" v-if="selectedCustomer">
                <div class="invoice-section--label">{{ $gettext('Project') }}</div>
                <div v-if="!selectedProject" class="text-right">
                  <ProjectSelect
                    ref="project-select"
                    :preselect="(preselectedProject) ? preselectedProject.project_id: null"
                    :customer-id="selectedCustomer.id"
                    allow-add searchable
                    @selected-project="selectProject"
                    @add-project="(projectName) => { newProject = projectName; showAddProjectModal = true }"
                  />
                  <GekkoButton
                    @click="showAddProjectModal = true"
                    :text="$gettext('Add new project')"
                    :icon="'fa-solid fa-plus'"
                    variant="link"
                    class="px-0 ml-auto"
                    size="sm"
                  />
                </div>
                <div v-else class="step-1--customer">
                  <div class="step-1--customer__customer" @click="showAddProjectModal = true">
                    <div><h4>{{ selectedProject.name }}</h4></div>
                    <div v-if="selectedProject.contact_name">{{ selectedProject.contact_name }}</div>
                    <div v-if="selectedProject.reference">{{ selectedProject.reference }}</div>
                    <GekkoButton
                      icon="fa-solid fa-xmark"
                      variant="link"
                      class="step-1-close"
                      @click="selectProject(null)"
                    />
                  </div>
                </div>
                
              </div>

            </div><!-- end step-1--customer-wrap -->



            <div v-if="selectedCustomer && !loadingCustomer" class="step-1--options-wrap">
              <div class="step-1--options gk-input-light" @click="showInvoiceDetailsModal = true" style="cursor: pointer; position: relative;">
                <div class="step-1--options__option">
                  <div class="invoice-section--label">{{ $gettext('Invoice Number') }}</div>
                  {{ invoice.number }}
                </div>
                <div class="step-1--options__option">
                  <div class="invoice-section--label">{{ $gettext('Invoice Date') }}</div>
                  {{ invoice.invoice_date }}
                </div>
                <!-- payment options -->
                <div v-if="!invoice.paid_up_front" class="step-1--options__option">
                  <div class="invoice-section--label">{{ $gettext('Payment term') }}</div>
                  {{ invoice.payment_term }} {{ $gettext('days') }}
                </div>
                <!-- TODO payment type (paid upfront) -->
                <div v-else-if="invoice.invoice_payment.payment_type" class="step-1--options__option">
                  <div class="invoice-section--label">{{ $gettext('Paid by') }}</div>
                  {{ paymentTypeOptions.find(opt => opt.value === invoice.invoice_payment.payment_type).text }}
                </div>
                <div class="step-1--options__option">
                  <div class="invoice-section--label">{{ $gettext('Project Reference') }}</div>
                  {{ (invoice.reference) ? invoice.reference : $gettext('empty') }}
                </div>

                <div style="position: absolute; top: 0.5rem; right: 1rem; font-size: 18px;">
                  <GekkoButton
                    :text="$gettext('Edit')"
                    icon="fa-solid fa-cog"
                    variant="link"
                    class="text-blue p-0 no-underline"
                  />
                </div>
              </div>
            </div><!-- end step-1--options-wrap -->



          </div><!-- end invoice-section step-1 -->

          <div v-if="selectedCustomer && !loadingCustomer" class="invoice-section step-2">
            <div class="invoice-section--lines">
              <InvoiceLines 
                v-model="invoice.invoice_lines"
                :invoice="invoice"
                ref="invoice-lines"
              />
              <div class="px-4">
                <GekkoButton
                  @click="showImport = true"
                  :text="$gettext('Import')"
                  icon="fa-solid fa-plus"
                />
              </div>
            </div>
            <div class="invoice-section--message">
              <div class="w-100 p-4">
                <b-form-group id="input-group-message" label-class="gk-label-margin gk-modal-label" label-for="input-message">
                  <b-input-group>
                    <b-form-textarea id="input-message" v-model="invoice.message" :placeholder="$gettext('extra message')" rows="6" />
                  </b-input-group>
                </b-form-group>
              </div>
            </div>
          </div>
        </div>
      </div> <!-- invoice-wrap-bg -->
    </overlay-scrollbars>
  </div>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from "vuex";

import InvoiceService from "@/store/services/invoice.service.js"
import InvoicePaymentsService from "@/store/services/invoice.payments.service.js"

import CustomerService from "@/store/services/customer.service.js"
import ProjectService from "@/store/services/project.service.js"

import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import InvoiceLines from "@/components/invoices/ui/InvoiceLines"

import CustomerSelect from "@/components/invoices/ui/CustomerSelect"
import ProjectSelect from "@/components/invoices/ui/ProjectSelect"
import CurrencyPicker from "@/components/CurrencyPicker"
import BrandSelector from "@/components/brands/BrandSelector"

import ImportToInvoiceModal from "@/components/invoices/ui/ImportToInvoiceModal"
import InvoiceDesignModal from "@/components/invoices/ui/InvoiceDesignModal"
import ImportKvkModal from "@/components/dashboard/ui/ImportKvkModal"
import ContactModalSimple from "@/components/contacts/ContactModalSimple"
import ProjectModal from "@/components/contacts/ProjectModal"
import BrandModal from "@/components/brands/BrandModal"
import InvoiceDetailsModal from "@/components/invoices/ui/InvoiceDetailsModal"

export default {
  name: "InvoiceAdd",
  components: {
    GekkoButton,
    LoadingSpinner,
    InvoiceLines,
    ImportToInvoiceModal,
    InvoiceDesignModal,
    CustomerSelect,
    ProjectSelect,
    CurrencyPicker,
    BrandSelector,
    ImportKvkModal,
    ContactModalSimple,
    ProjectModal,
    BrandModal,
    InvoiceDetailsModal
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      invoice: {},
      loading: true,
      loadingCustomer: false,
      selectedCustomer: null,
      selectedProject: null,
      preselectedCustomer: null,
      preselectedProject: null,
      newCustomer: null,
      newProject: null,
      
      showImport: false,
      showDesign: false,
      showContactModal: false,
      showAddProjectModal: false,
      showCustomerSelector: true,
      showImportKvkModal: false,
      showEditBrandModal: false,
      showBrandsSelector: false,
      showInvoiceDetailsModal: false,

      showTAXWarning: false,
      showingTAXWarning: false,

      targetData: [
        { key: 'address', label: this.$gettext('Address') },
        { key: 'bank_account_number', label: this.$gettext('Bank account number') },
        { key: 'btw', label: this.$gettext('Btw number') },
        { key: 'city', label: this.$gettext('City') },
        { key: 'country', label: this.$gettext('Country') },
        { key: 'email', label: this.$gettext('Email') },
        { key: 'legal_form', label: this.$gettext('Legal form') },
        { key: 'name', label: this.$gettext('Name') },
        { key: 'phone_number', label: this.$gettext('Phone number') },
        { key: 'postal_code', label: this.$gettext('Postal code') },
      ],
      paymentTypeOptions: [
        { value: '', text: this.$gettext('Select a payment type') },
        { value: 'cash', text: this.$gettext('Cash or private account') },
        { value: 'bank', text: this.$gettext('Bank transaction') }
      ],
    }
  },
  computed: {
    ...mapGetters({
      storeInvoice: 'invoices/getInvoice',
      invoicePayments: 'invoices/getInvoicePayments',
      currentCompany: "main/getCurrentCompany",
      currentLayout: "invoicesLayout/getCurrentLayout",
      languageOptions: "main/getInvoiceLanguageOptions",
      availableBrands: "brands/getAvailableBrands",
      brandById: "brands/getBrandById",
      ueCountryCodes: "main/getUeCountryCodes",
      companyTaxRates: "invoices/getCompanyTaxRates"
    }),
    selectedBrand () {
      return (this.invoice.brand) ? this.brandById(this.invoice.brand) : null
    },
    // TODO remove this completely?
    // scheduledForToday () {
    //   return this.recurrent && this.recurrent.start_date == Date.today().format("Y-m-d")
    // },
    showKvkWarning () {
      return (this.companyMissingFields.length > 0)
    },
    companyMissingFields () {
      let missing_fields = []
      this.targetData.forEach(dataItem => {
        if (!this.currentCompany[dataItem.key]) {
          missing_fields.push(dataItem)
        }
      })
      return missing_fields
    },
    companyMissingFieldsText () {
      return this.companyMissingFields.map(obj => obj.label).join(', ')
    },
    selectedPaymentTypeText () {
      let type_text = ''
      if (this.invoice.invoice_payment.payment_type) {
        type_text = this.paymentTypeOptions.find(opt => opt.value === this.invoice.invoice_payment.payment_type).text
      }
      return type_text
    }
  },
  watch: {
    'invoice.invoice_lines': {
      handler (lines) {
        this.checkLinesForWarnings(lines)
      },
      deep: true
    },
    showTAXWarning (newValue) {
      if (newValue) {
        switch (newValue) {
          case 'NLTaxWarning2019Other':
            this.$store.commit('setAlert', {
              title: this.$gettext('VAT Warning'),
              text: this.$gettext('Inside the Netherlands, the VAT percentage is in most cases either 21% or 9%. Please make sure your current VAT is indeed correct.'),
              variant: 'warning',
              duration: 10
            })
            break;
          case 'IntTaxWarning':
            this.$store.commit('setAlert', {
              title: this.$gettext('VAT Warning'),
              text: this.$gettext('When you invoice foreign entities, you usually do not use normal VAT percentages that you charge to local customers. Often you reverse charge your VAT to the foreign entity. Please make sure you are charging VAT correctly.'),
              variant: 'warning',
              duration: 10
            })
            break;
        }
      }
    }
  },
  created () {
    console.log('invoiceadd created')
    this.loading = true
    this.setupData()

    const kvkTargetItem = { key: 'kvk', label: this.currentCompany.coc_label_short }
    this.targetData.push(kvkTargetItem)
  },
  methods: {
    ...mapMutations({ 
      setRecipient: 'invoices/SET_INVOICE_CREATE_RECIPIENT',
      setProject: 'invoices/SET_INVOICE_CREATE_PROJECT',
      setInvoicePayment: 'invoices/SET_INVOICE_PAYMENT',
      setInvoicePaidUpfront: 'invoices/SET_INVOICE_PAID_UPFRONT'
    }),
    ...mapActions ({
      getNextInvoiceNumber: "invoices/getNextInvoiceNumber",
      loadInvoice: 'invoices/loadInvoice',
      loadCompanyBrands: 'brands/loadCompanyBrands'
    }),
    setupData () {
      this.loading = true
      if (this.isEdit && this.storeInvoice) {
        this.invoice = Object.assign({}, this.storeInvoice)
        console.log('invoice to edit ->', this.invoice)
        // select customer
        this.preselectedCustomer = { name: '', customer_id: null, project_id: null }
        if (this.invoice.recipient_customer) {
          this.preselectedCustomer.customer_id = this.invoice.recipient_customer.id
          this.preselectedCustomer.name = this.invoice.recipient_customer.name
        }
        this.preselectedProject = { name: '', project_id: null }
        if (this.invoice.project) {
          this.preselectedProject.project_id = this.invoice.project.id
          this.preselectedProject.name = this.invoice.project.name
        }
        this.setInvoicePayment({
          payment_type: 'cash',
          amount: 0,
          payment_date: ''
        })
        if (this.invoice.paid_up_front) {
          this.setInvoicePayments()
        }
        this.invoice.invoice_payment = this.storeInvoice.invoice_payment
        this.loading = false
      } else {
        this.invoice = {
          number: null,
          invoice_date: new Date().format('Y-m-d'),
          payment_term: this.currentCompany.default_payment_term,
          sender_company: this.currentCompany.id,
          vat_reversed_charge: this.currentCompany.default_tax_rate_reversed,
          line_amount_is_gross: true,
          recipient_customer: null,
          invoice_lines: [],
          language: 'nl', // TODO: for now 'nl' is default, take it from customer in the future
          currency: 'EUR',
          brand: (this.selectedBrand) ? this.invoice.brand: null,
          paid_up_front: false,
          invoice_payment: {
            payment_type: 'cash',
            amount: 0,
            payment_date: ''
          }
        }
        const invoice_year = { 'year': new Date().format('Y') }
        this.getNextInvoiceNumber(invoice_year).then((response) => {
          this.invoice.number = response
          this.loading = false
        })
      }
    },
    closeDesignModal () {
      this.showDesign = false
    },
    closeImportModal () {
      this.showImport = false
    },
    prepareUpdatedCustomer (customer) {
      const selected = {
        name: customer.name,
        customer_id: customer.id,
        project_id: null
      }
      this.selectCustomerAndProject(selected)
    },
    selectCustomerAndProject (selected) {
      if (selected.customer_id) {
        // get full customer info
        this.getCustomerData(selected)
        this.$emit('display-buttons', 'create')
      } else {
        this.selectedCustomer = null
        this.$emit('display-buttons', '')
      }
    },
    selectProject (selected, query = true) {
      if (selected && !query) {
        this.selectedProject = selected
        this.invoice.reference = this.selectedProject.reference
        this.invoice.project = this.selectedProject.id
        this.setProject(this.selectedProject)
      } else if (selected && selected.value) {
        ProjectService.retrieve(selected.value).then((response) => {
          this.selectedProject = response.data
          this.invoice.reference = this.selectedProject.reference
          this.invoice.project = this.selectedProject.id
          this.setProject(this.selectedProject)
        })
      } else {
        this.preselectedProject = { name: '', project_id: null }
        this.selectedProject = null
        this.invoice.reference = null
        this.invoice.project = null
        this.setProject(null)
      }
    },
    selectBrand (brand_id) {
      this.invoice.brand = brand_id
    },
    selectCurrency (currency) {
      this.invoice.currency = currency
    },
    resetCustomer () {
      this.selectedCustomer = null
      this.selectedProject = null
      this.invoice.invoice_lines.splice(0)
      this.$emit('display-buttons', '')
      this.setupData()
    },
    getCustomerData (selected) {
      this.loadingCustomer = true
      CustomerService.retrieve(selected.customer_id).then((response) => {
        this.selectedCustomer = response.data

        this.invoice.recipient_customer = selected.customer_id

        if (this.selectedCustomer.country === this.currentCompany.country) {
          this.invoice.vat_reversed_charge = this.currentCompany.default_tax_rate_reversed
        } else if (this.ueCountryCodes.includes(this.selectedCustomer.country)) {
          this.invoice.vat_reversed_charge = true
        }

        this.setRecipient(this.selectedCustomer)

        // set customer's payment_term
        if (this.selectedCustomer.default_payment_term && this.selectedCustomer.default_payment_term != this.currentCompany.default_payment_term ) {
          this.invoice.payment_term = this.selectedCustomer.default_payment_term
        } else {
          this.invoice.payment_term = this.currentCompany.default_payment_term
        }

        this.loadingCustomer = false
      })
    },
    getTotals () {
      const totals = {
        vat_totals: [],
        subtotal: 0,
        total: 0
      }
      let vat_totals = []
      if (this.$refs['invoice-lines']) {
        vat_totals = this.$refs['invoice-lines'].vatTotals
        vat_totals = Object.entries(vat_totals).map(([key, value]) => [parseInt(key), value])
        totals.vat_totals = vat_totals
        totals.subtotal = this.$refs['invoice-lines'].subtotal
        totals.total = this.$refs['invoice-lines'].total
      }

      return totals
    },
    addLines (lines) {
      this.$refs['invoice-lines'].addLinesViaImport(lines)
    },
    saveInvoice () {
      console.log('saveInvoice', this.invoice)

      // LINE VALIDATION
      let line_errors = false
      if (this.invoice.invoice_lines.length === 1 && this.invoice.invoice_lines[0].default) {
        line_errors = true
      } else {
        this.invoice.invoice_lines.forEach(line => {
          if (!line.default && line.amount === '') {
            line_errors = true
          }
        })
      }

      if (line_errors) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Could not save'),
          text: this.$gettext('Please review invoice line amounts'),
          variant: 'danger',
          duration: 4
        })
        return
      }
      
      // TODO TEST this
      if (this.invoice.currency != 'EUR' && this.invoice.paid_up_front) {
        if (!(this.invoice.total_amount > 0) && parseFloat(this.$refs['invoice-lines'].total) > 0) {
          this.editingDetails = true
          this.invoice.errors.total_amount = this.$gettext("This invoice is in a foreign currency and marked as paid. Please provide the invoice total in euro's.")
          return
        } else {
          this.invoice.invoice_payment.amount = this.invoice.total_amount
        }
      }

      // Prepare lines
      this.prepareLinesForSend()

      let saveInvoicePromise, invoiceResponse

      this.__updateInvoicePayment();

      if (this.invoice.id == null) {
        // CREATE
        saveInvoicePromise = InvoiceService.create(this.invoice).then((response) => {
          invoiceResponse = response.data
          if (this.invoice.paid_up_front) {
              this.invoice.invoice_payment.invoice = invoiceResponse.id
              return InvoicePaymentsService.create(this.invoice.invoice_payment)
          }
          return invoiceResponse
        }).catch((response) => {
          if (invoiceResponse) {
            return InvoiceService.destroy(invoiceResponse.id)
          }
          throw response
        })
      } else {
        // EDIT
        console.log('this.invoice', this.invoice)
        saveInvoicePromise = InvoiceService.update(this.invoice.id, this.invoice).then((response) => {
          invoiceResponse = response.data
          this.invoice.invoice_payment.invoice = invoiceResponse.id
          if (this.invoice.paid_up_front) {
            if (this.invoice.invoice_payment.id) {
              return InvoicePaymentsService.partial_update(this.invoice.invoice_payment)
            } else {
              return InvoicePaymentsService.create(this.invoice.invoice_payment)
            }
          } else {
            if (this.invoice.invoice_payment.id) {
              return InvoicePaymentsService.destroy(this.invoice.invoice_payment.id)
            }
          }
          return response
        })
      }

      saveInvoicePromise.then(() => {
        this.__successFunction(invoiceResponse)
      }, this.__failFunction)
    },
    // TODO test this
    __updateInvoicePayment() {
      console.log('__updateInvoicePayment', this.invoice)
      if (this.invoice.currency == 'EUR') {
        this.invoice.invoice_payment.amount = this.$refs['invoice-lines'].total
      }
      this.invoice.invoice_payment.payment_date = this.invoice.invoice_date
    },
    __successFunction(invoice) {
      console.log('__successFunction', invoice)

      this.loadInvoice(invoice.id).then(() => {
        this.$router.push({ name: 'InvoiceView', params: { invoiceId: invoice.id } }).catch(() => {})
      })
      this.$emit('invoice-updated')
    },
    __failFunction(data) {
      // TODO display errors
      // vm.object.errors = data.data;
      this.$refs['invoice-lines'].addNewLine()
      console.log('__failFunction', data)
    },
    prepareLinesForSend () {
      if (this.invoice.vat_reversed_charge) {
        this.invoice.invoice_lines.forEach(line => {
          line.tax_rate = 0
        })
      }
      // deregister the $watch on lines by calling it again
      this.$refs['invoice-lines'].removeLineWatch()

      // remove last empty line
      this.removeLastDefaultLine(this.invoice)
    },
    /**
     * Removes last invoice line if it's default
     */
    prepareInvoicePreview () {
      const prepared_invoice = JSON.parse(JSON.stringify(this.invoice))
      return this.removeLastDefaultLine(prepared_invoice)
    },
    removeLastDefaultLine (invoice) {
      const invoice_lines = invoice.invoice_lines
      if (invoice_lines.length > 0 && invoice_lines[invoice_lines.length - 1].default) {
        invoice_lines.splice(invoice_lines.length - 1, 1)
      }
      return invoice
    },
    setInvoicePayments () {
      if (this.invoicePayments && this.invoicePayments.length > 0 && this.invoicePayments[0].payment_type) {
        this.setInvoicePayment(this.invoicePayments[0])
      } else {
        this.setInvoicePaidUpfront(false)
      }
    },
    openEditSenderModal () {
      console.log('openEditSenderModal', this.selectedBrand)
      if (this.selectedBrand) {
        this.openBrandModal()
      } else {
        this.showImportKvkModal = true
      }
    },
    openBrandModal () {
      this.$store.commit('brands/SET_SELECTED_BRAND_NAME', this.selectedBrand.name)
      this.$store.commit('brands/SET_SELECTED_BRAND_ID', this.selectedBrand.id)
      this.showEditBrandModal = true
    },
    closeBrandModal () {
      this.showEditBrandModal = false
      this.$store.commit('brands/SET_SELECTED_BRAND_NAME', '')
      this.$store.commit('brands/SET_SELECTED_BRAND_ID', 0)
    },
    onBrandCreated (brand) {
      this.selectBrand(brand.id)
      this.showEditBrandModal = false
      this.loadCompanyBrands(this.currentCompany.id)
    },
    onBrandUpdated () {
      this.showEditBrandModal = false
      this.loadCompanyBrands(this.currentCompany.id)
    },
    onBrandDeleted () {
      this.showEditBrandModal = false
      this.selectedBrand = null
      this.loadCompanyBrands(this.currentCompany.id)
    },
    closeProjectModal () {
      this.showAddProjectModal = false
    },
    onProjectCreated (payload) {
      this.closeProjectModal()
      this.selectProject({ value: payload.id })
    },
    onProjectUpdated (payload) {
      this.selectProject(payload, false)

      this.closeProjectModal()
    },
    onProjectDeleted () {
      this.closeProjectModal()
      this.selectProject(null)
    },
    checkLinesForWarnings (lines) {
      console.log('checkLinesForWarnings')
      this.showTAXWarning = false

      if (lines.length === 1 && lines[0].default) {
        this.showTAXWarning = false
      } else {

        if (this.selectedCustomer && this.selectedCustomer.country) {
          lines.forEach(line => {
            if (this.selectedCustomer.country == 'NL') {
              // check for NLTaxWarning2019Other
              if (line.tax_rate != null && ([21, 9].indexOf(line.tax_rate) == -1) || line.vat_exempt || this.invoice.vat_reversed_charge) {
                this.showTAXWarning = 'NLTaxWarning2019Other'
              }
            } else {
              // not NL recipient, check for IntTaxWarning
              if (!(line.tax_rate == null || line.tax_rate == 0 || line.vat_exempt || this.invoice.vat_reversed_charge)) {
                this.showTAXWarning = 'IntTaxWarning'
              }
            }
          })
        }

      }

    }
  }
}
</script>
<style scoped lang="scss">
  .invoice-add-wrap {
    height: 100%;
    margin: 0 auto;
    border: none !important;
    border-radius: 0 !important;

    .gk-alert {
      font-size: 14px; 
      padding: 0.5rem;
    }
    .invoice-wrap {
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 100%;
      width: 100%;
    }
    .invoice-wrap-bg {
      @media (min-width: '992px') {
        background: $colorBgGrey;
        padding: 1rem;
        min-height: 100%;
        .invoice-wrap {
          min-height: 100vh;
          max-width: 1200px;
          margin: 0 auto;
          border-radius: $radiusLg;
          background: white;
          box-shadow: $dropdownShadow;
        }
      }
    }
    .invoice-section {
      display: flex;
      gap: 1rem;
      flex-direction: column;
      &--label {
        margin-bottom: 0;
        font-size: 14px;
        color: $colorTextBase;
      }
      &.step-1 {
        padding: 0rem;
        gap: 0;
        display: flex;
        justify-content: center;

        .step-1--sender-wrap,
        .step-1--recipient-wrap,
        .step-1--project-wrap {
          width: 100%;
          @media (min-width: '768px') { 
            width: 50%;
          }
          @media (min-width: '992px') { 
            width: 33%;
          }
        }

        .step-1--customer-wrap,
        .step-1--options-wrap {
          @media (min-width: '992px') { 
            width: 100%;
          }
        }
        
        .step-1--sender-wrap,
        .step-1--recipient-wrap,
        .step-1--project-wrap {
          display: flex;
          flex-direction: column;
        }
        &.has-customer {
          .step-1--customer-wrap {
            flex: 1;
            @media (min-width: '992px') { 
              width: 100%;
            }
          }
        }
        .step-1--customer-wrap {
          display: flex;
          font-size: 14px;
          padding: 1rem;
          gap: 1rem;
          flex-direction: column;
          // max-width: 900px;
          // margin: 0 auto;
          @media (min-width: '576px') {
            flex-direction: row;
          }
          .invoice-section--label {
            font-size: 1rem;
            margin-bottom: 0.25rem;
          }
        }
        .step-1--sender {
          display: flex;
          position: relative;
          // gap: 1rem;
          border-radius: $radiusMd;
          outline: 1px solid $colorBorderDash;
          &.has-brand {
            padding-right: 2rem;
          }
          &__info {
            flex: 1;
            .current-company-name {
              .brands-company {
                font-size: 14px;
                opacity: 0.5;
              }
            }
            .field-label {
              font-family: DINNextLTPro-Medium, sans-serif;
            }
          }
          &__logo {
            width: 75px;
            height: 75px;
            position: relative;
            margin-top: 4px;
            border: 1px solid $colorBorderDash;
            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              object-position: top;
            }
            .no-logo {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;
              background: #e7e7e7;
            }
          }
          .invoice-brand-select-wrap {
            padding: 0.5rem;
            padding-top: 0;
            &.show-selector {
              padding: 0.5rem;
            }
            .invoice-section--label {
              font-size: 14px;
            }
          }
        }
        .step-1--customer {
          position: relative;
          display: flex;
          flex: 1;
          &__customer {
            flex: 1;
            background: $colorBgGrey;
            border-radius: $radiusMd;
            padding: 0.5rem;
            padding-right: 2rem;
            cursor: pointer;
          }
        }
        .step-1--customer
        .step-1--options-wrap {
          display: flex;
        }
        .step-1--options-wrap {
          background: #f9f9f9;
        }
        .step-1--options {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          width: 100%;
          padding: 1rem;
          @media (min-width: '576px') {
            grid-template-columns: 1fr 1fr;
          }
          @media (min-width: '768px') { 
            grid-template-columns: 1fr 1fr 1fr;
          }
          @media (min-width: '992px') { 
            grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
          }
        }
        .b-form-datepicker { height: 38px; }
        .step-1-close {
          padding: 0;
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          border: 0;
          line-height: 0;
          color: $colorTextBase;
          opacity: 0.3;
          font-size: 14px;
          z-index: 10;
        }
      }
      &.step-2 { 
        flex-direction: column;
        border-top: 1px solid $colorBorderDash;
      }
      &.gekkofooter { justify-content: flex-end; }
    }
    .invoice-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }
</style>

<style lang="scss">
.invoice-section--lines {
  overflow-x: auto;
  .gk-table-invoicelines thead td:first-child {
    border-top-left-radius: 0;
    padding-left: 1.5rem;
  }
  .gk-table-invoicelines thead td:last-child {
    border-top-right-radius: 0;
  }
  .gk-table-invoicelines .col-quantity {
    padding-left: 1.5rem;
  }
}
</style>